import React, { useEffect, useMemo, useState } from "react";
import { AlertTriangle, MapPinOff, Info, Building2, Home } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Skeleton } from "./ui/skeleton";
import { api, errMsg } from "../lib/api";
import { StaffAvatar, EmpCode } from "./StaffAvatar";

// Phase 159 — Bulk Unassign Territories (Users & Staff, Municipal Admin only).
// Flow: open -> dry-run plan (who will be cleared, who is skipped and why, properties currently assigned)
//       -> admin types the confirm word -> POST -> toast + onDone(result).
// Supervisors are skipped by design (their zones are not touched); collectors without territory have nothing to clear.
const CONFIRM_WORD = "UNASSIGN";

const wardChip = (w) => (w.mode === "mohallas" ? `${w.name} (${w.mohallas} mohalla${w.mohallas === 1 ? "" : "s"})` : w.name);

export function BulkUnassignDialog({ code, userIds, open, onOpenChange, onDone }) {
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState(null);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) { setPlan(null); setError(null); setTyped(""); setBusy(false); return; }
    let alive = true;
    api.post(`/t/${code}/users/territory/bulk-unassign`, { user_ids: userIds, dry_run: true })
      .then((r) => { if (alive) setPlan(r.data); })
      .catch((e) => { if (alive) setError(errMsg(e)); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, code, (userIds || []).join("|")]);

  const eligible = plan?.eligible || [];
  const skipped = plan?.skipped || [];
  const totals = plan?.totals || {};
  const confirmed = typed.trim().toUpperCase() === CONFIRM_WORD;
  const canRun = !!plan && eligible.length > 0 && confirmed && !busy;

  const wardsTouched = useMemo(() => (plan?.affected_wards || []).map((w) => w.name), [plan]);

  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/t/${code}/users/territory/bulk-unassign`, { user_ids: userIds, confirm: typed.trim().toUpperCase() });
      const n = r.data?.totals?.unassigned ?? r.data?.unassigned?.length ?? 0;
      const sk = (r.data?.skipped || []).length;
      toast.success(`Territory cleared for ${n} collector${n === 1 ? "" : "s"}${sk ? ` · ${sk} skipped` : ""}`, {
        description: r.data?.assignment_sync === "started"
          ? `Their properties are being re-synced across ${r.data.assignment_sync_wards} ward${r.data.assignment_sync_wards === 1 ? "" : "s"} — anything nobody covers now shows under Coverage Alerts.`
          : undefined,
      });
      onDone?.(r.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="bulk-unassign-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <MapPinOff className="h-5 w-5 text-destructive" /> Unassign all territories
          </DialogTitle>
          <DialogDescription data-testid="bulk-unassign-description">
            Removes every ward and mohalla mapping from the selected tax collectors in one action. Their properties are then
            re-synced to whoever still covers them; properties nobody covers are flagged under Coverage Alerts.
            Supervisors keep their zones — this action never changes zone mappings.
          </DialogDescription>
        </DialogHeader>

        {error ? (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive" data-testid="bulk-unassign-error">
            {error}
          </div>
        ) : !plan ? (
          <div className="space-y-2" data-testid="bulk-unassign-loading">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-8 w-2/3" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* summary strip */}
            <div className="grid grid-cols-3 gap-2" data-testid="bulk-unassign-totals">
              <div className="rounded-lg border bg-destructive/5 border-destructive/20 p-2.5">
                <p className="text-[11px] text-muted-foreground">Will be unassigned</p>
                <p className="text-lg font-semibold tabular-nums leading-tight text-destructive" data-testid="bulk-unassign-total-eligible">{totals.eligible ?? eligible.length}</p>
              </div>
              <div className="rounded-lg border bg-muted/40 p-2.5">
                <p className="text-[11px] text-muted-foreground">Skipped</p>
                <p className="text-lg font-semibold tabular-nums leading-tight" data-testid="bulk-unassign-total-skipped">{totals.skipped ?? skipped.length}</p>
              </div>
              <div className="rounded-lg border bg-muted/40 p-2.5">
                <p className="text-[11px] text-muted-foreground">Properties affected</p>
                <p className="text-lg font-semibold tabular-nums leading-tight" data-testid="bulk-unassign-total-properties">{totals.assigned_properties ?? 0}</p>
              </div>
            </div>

            {/* eligible */}
            <section className="space-y-1.5">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5 text-destructive" /> Territory will be cleared ({eligible.length})
              </h4>
              {eligible.length === 0 ? (
                <p className="text-sm text-muted-foreground rounded-lg border border-dashed p-3" data-testid="bulk-unassign-none-eligible">
                  None of the selected users has ward / mohalla territory to unassign.
                </p>
              ) : (
                <ul className="rounded-lg border divide-y max-h-[32vh] overflow-y-auto overscroll-contain" data-testid="bulk-unassign-eligible-list">
                  {eligible.map((u) => (
                    <li key={u.id} className="flex items-start gap-3 p-2.5" data-testid={`bulk-unassign-eligible-${u.email}`}>
                      <StaffAvatar code={code} userId={u.id} name={u.name} size="sm" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium leading-tight">
                          {u.name}<EmpCode code={u.employee_id} />
                          {u.is_sub && <Badge variant="outline" className="ml-1.5 text-[10px] align-middle">sub-user</Badge>}
                          {u.is_active === false && <Badge variant="outline" className="ml-1.5 text-[10px] align-middle text-muted-foreground">inactive</Badge>}
                        </div>
                        <div className="flex flex-wrap items-center gap-1 mt-1" data-testid={`bulk-unassign-wards-${u.email}`}>
                          <Building2 className="h-3 w-3 text-muted-foreground shrink-0" />
                          {u.wards.map((w) => (
                            <Badge key={w.id} variant="outline" className={`text-[10px] font-normal ${w.mode === "mohallas" ? "bg-sky-50 text-sky-800 border-sky-200" : ""}`}>
                              {wardChip(w)}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <span className="shrink-0 text-right text-xs text-muted-foreground inline-flex items-center gap-1" title="Properties currently assigned to this collector"
                        data-testid={`bulk-unassign-props-${u.email}`}>
                        <Home className="h-3 w-3" /> <span className="tabular-nums font-medium text-foreground">{u.assigned_properties}</span> propert{u.assigned_properties === 1 ? "y" : "ies"}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* skipped */}
            {skipped.length > 0 && (
              <section className="space-y-1.5">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                  <Info className="h-3.5 w-3.5" /> Skipped — nothing changes ({skipped.length})
                </h4>
                <ul className="rounded-lg border divide-y bg-muted/30 max-h-[20vh] overflow-y-auto overscroll-contain" data-testid="bulk-unassign-skipped-list">
                  {skipped.map((u) => (
                    <li key={u.id} className="flex items-start gap-3 p-2 text-sm" data-testid={`bulk-unassign-skipped-${u.email || u.id}`}>
                      <StaffAvatar code={code} userId={u.id} name={u.name || "?"} size="xs" />
                      <div className="min-w-0 flex-1">
                        <p className="leading-tight">
                          {u.name || "Unknown user"}{u.employee_id && <EmpCode code={u.employee_id} />}
                          <span className="ml-1.5 capitalize text-[11px] text-muted-foreground">{(u.role || "").replace(/_/g, " ")}</span>
                        </p>
                        <p className="text-[11px] text-muted-foreground mt-0.5">{u.reason}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {eligible.length > 0 && (
              <>
                {wardsTouched.length > 0 && (
                  <p className="text-xs text-muted-foreground" data-testid="bulk-unassign-wards-touched">
                    Properties in <span className="font-medium text-foreground">{wardsTouched.slice(0, 6).join(", ")}{wardsTouched.length > 6 ? ` +${wardsTouched.length - 6} more` : ""}</span> will be re-synced after this action.
                  </p>
                )}
                <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 space-y-2">
                  <Label htmlFor="bulk-unassign-confirm-input" className="text-sm">
                    This cannot be undone in one click — each collector would need to be mapped again. Type <span className="font-mono font-semibold">{CONFIRM_WORD}</span> to confirm.
                  </Label>
                  <Input id="bulk-unassign-confirm-input" value={typed} onChange={(e) => setTyped(e.target.value)}
                    placeholder={CONFIRM_WORD} autoComplete="off" spellCheck={false} disabled={busy}
                    className="font-mono tracking-widest uppercase bg-background" data-testid="bulk-unassign-confirm-input"
                    aria-invalid={typed.length > 0 && !confirmed} />
                </div>
              </>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid="bulk-unassign-cancel">Cancel</Button>
          <Button variant="destructive" onClick={run} disabled={!canRun} data-testid="bulk-unassign-submit">
            <MapPinOff className="h-4 w-4 mr-2" />
            {busy ? "Unassigning…" : `Unassign ${eligible.length || ""} ${eligible.length === 1 ? "collector" : "collectors"}`.replace(/\s+/g, " ")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
